import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { KeyboardAvoidingView as KeyboardAvoidingViewRNKC, KeyboardStickyView, } from 'react-native-keyboard-controller';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import KeyboardAvoidingView from 'design-system/KeyboardAvoidingView';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useBottomTabHeight from 'hooks/useBottomTabHeight';
import { useIsTopScreen } from 'hooks/navigation/useIsTopScreen';
import { rem } from './values';
import { isWeb } from '../constants';
import { HEIGHT as BUTTON_HEIGHT } from './Button';
const BOTTOM_OFFSET = rem(16);
/**
 * For screens with a single button, use `KeyboardAvoidingButton` outside the component and
 * pass `withButtonSpace` and `flexContainer` to `KeyboardAvoidingContainerNew`.
 *
 * `KeyboardAvoidingButton` uses a sticky container which has smoother animations for this use case.
 *
 * Example:
 *
 * ```
 * ...
 * <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
 *   {children}
 * </KeyboardAvoidingContainerNew>
 * <KeyboardAvoidingButton  />
 * ...
 * ```
 *
 * For input screens which should be centered and avoid the keyboard, use the flexContainer prop
 *
 * Example:
 *
 * ```
 * <KeyboardAvoidingContainerNew flexContainer>
 *    {children}
 * </KeyboardAvoidingContainerNew>
 *```
 *
 * For screens with multiple buttons, use `KeyboardAvoidingContainerNew` and pass
 * sticky prop to make content stick to the keyboard.
 *
 * Example:
 *
 * ```
 * <View>
 *    {children}
 *    <KeyboardAvoidingContainerNew sticky>
 *      <SomethingElse />
 *    </KeyboardAvoidingContainerNew>
 * </View>
 * ```
 */
export const KeyboardAvoidingContainerNew = ({ isInTab, isInIOS13Modal, flexContainer, withFrame, withMarginHorizontal = true, children, sticky, withButtonSpace, stickyClosedOffset = 0, stickyOpenedOffset = 0, }) => {
    const { marginBottom } = useMarginBottom('marginBottom');
    // Only the screen on top follows the keyboard: the one behind it keeps its own layout
    // while it is still visible, e.g. during a back swipe to it.
    const isTopScreen = useIsTopScreen();
    const bottomTabBarHeight = useBottomTabHeight();
    // eslint-disable-next-line no-nested-ternary
    const marginBottomOffset = isInTab
        ? rem(16)
        : typeof marginBottom === 'number'
            ? marginBottom
            : 0;
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb || isInTab, isInIOS13Modal);
    const containerStyle = useMemo(() => [
        withFrame && paddingHorizontalStyle,
        withMarginHorizontal && styles.container,
        flexContainer && styles.flexContainer,
        !sticky && { marginBottom },
    ], [
        withFrame,
        paddingHorizontalStyle,
        withMarginHorizontal,
        flexContainer,
        sticky,
        marginBottom,
    ]);
    if (sticky) {
        return (<KeyboardStickyView enabled={isTopScreen} offset={{
                closed: marginBottomOffset || stickyClosedOffset
                    ? -(marginBottomOffset + stickyClosedOffset)
                    : undefined,
                opened: -(BOTTOM_OFFSET +
                    stickyOpenedOffset -
                    (isInTab ? bottomTabBarHeight : 0)),
            }} style={containerStyle}>
        {children}
      </KeyboardStickyView>);
    }
    if (Platform.OS === 'ios') {
        return (<KeyboardAvoidingView enabled={isTopScreen} behavior="padding" keyboardVerticalOffset={BOTTOM_OFFSET} style={containerStyle}>
        {children}
        {!!withButtonSpace && <View style={styles.buttonSpace}/>}
      </KeyboardAvoidingView>);
    }
    return (<KeyboardAvoidingViewRNKC enabled={isTopScreen} behavior="padding" keyboardVerticalOffset={BOTTOM_OFFSET} style={containerStyle}>
      {children}
      {!!withButtonSpace && <View style={styles.buttonSpace}/>}
    </KeyboardAvoidingViewRNKC>);
};
const styles = StyleSheet.create({
    container: {
        marginHorizontal: rem(16),
    },
    flexContainer: {
        flex: 1,
    },
    buttonSpace: {
        height: BUTTON_HEIGHT,
    },
});
